import React, { useMemo, useCallback } from 'react';
import {
  Line,
  ReferenceLine,
  Tooltip as RechartsTooltip,
} from 'recharts';
import EquityChart from '../EquityChart/EquityChart';
import MetricsRow from '../MetricsRow';
import {
  createBacktestEquityViewModel,
  formatAxisDate,
  formatCurrencyValue,
  formatPercentValue,
  formatSignedCurrencyValue,
  REPLAY_X_AXIS_TICKS,
} from './backtestEquityViewModel';
import './BacktestEquityDisplay.css';

function BacktestEquityTooltip({ active, payload, showReturn }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <div className="bed-tooltip">
      {point?.date && <div className="bed-tooltip__date">{point.date}</div>}
      <div className="bed-tooltip__value">{formatCurrencyValue(point?.y)}</div>
      {showReturn && point?.returnPct !== null && point?.returnPct !== undefined && (
        <div className="bed-tooltip__return">{formatPercentValue(point.returnPct)}</div>
      )}
    </div>
  );
}

export default function BacktestEquityDisplay({
  mode = 'completed',
  points = [],
  result = {},
  progress = null,
  replayProgress = 1,
  metrics = null,
  showBenchmark = false,
  loading = false,
  error = null,
  height = 336,
  showMetrics = false,
  metricsSize = 'default',
  className = '',
}) {
  const model = useMemo(
    () =>
      createBacktestEquityViewModel({
        mode,
        points,
        result,
        progress,
        replayProgress,
        metrics,
      }),
    [mode, points, result, progress, replayProgress, metrics],
  );

  const tickFormatter = useCallback(
    (value) => {
      if (model.isPlayback) return model.getReplayTickLabel(value);
      return formatAxisDate(value, model.axisDateGranularity);
    },
    [model],
  );

  const chartColor =
    model.trendDirection === 'negative'
      ? 'var(--backtest-danger, #ef4444)'
      : model.trendDirection === 'positive'
        ? 'var(--backtest-success, #10b981)'
        : 'var(--backtest-primary, #3b82f6)';

  const rootClass = ['bed', `bed--${mode}`, `bed--${model.trendDirection}`, className]
    .filter(Boolean)
    .join(' ');

  const signedGain = formatSignedCurrencyValue(model.gainLoss);
  const signedGainPct = formatPercentValue(model.gainLossPct);
  const finalValue = formatCurrencyValue(model.finalValue);
  const startValue = formatCurrencyValue(model.startValue);
  const chartProgressLabel =
    model.progressPercent === null ? null : Number(model.progressPercent.toFixed(1)).toString();
  const chartAriaLabel =
    mode === 'running' && chartProgressLabel !== null
      ? `Backtest equity chart with ${model.chartData.length} points through ${chartProgressLabel}%`
      : 'Backtest equity chart';
  const isCompletedChart = mode === 'completed';

  return (
    <div className={rootClass}>
      {mode === 'completed' ? (
        <div className="bed-completed-summary" aria-label="Backtest starting and final equity">
          <div className="bed-completed-summary__item">
            {model.startDate && (
              <span className="bed-completed-summary__date">{model.startDate}</span>
            )}
            {startValue && <span className="bed-completed-summary__value">{startValue}</span>}
          </div>
          <span className="bed-completed-summary__arrow" aria-hidden="true">
            →
          </span>
          <div className="bed-completed-summary__item bed-completed-summary__item--final">
            {model.endDate && <span className="bed-completed-summary__date">{model.endDate}</span>}
            {finalValue && <span className="bed-completed-summary__value">{finalValue}</span>}
            {signedGain && (
              <span
                className={`bed-completed-summary__gain bed-gain bed-gain--${model.trendDirection}`}
              >
                {signedGain}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="bed-live-summary" aria-label="Current simulated backtest equity">
          {finalValue && <div className="bed-live-summary__value">{finalValue}</div>}
          {signedGain && signedGainPct && (
            <div className={`bed-live-summary__gain bed-gain bed-gain--${model.trendDirection}`}>
              {signedGain} ({signedGainPct})
            </div>
          )}
          <div className="bed-live-summary__meta">
            {model.endDate && <span>{model.endDate}</span>}
          </div>
        </div>
      )}

      <div
        className={`bed-chart trend-${model.trendDirection}`}
        data-testid="backtest-chart"
        aria-label={chartAriaLabel}
        style={{ minHeight: height }}
      >
        {loading && <div className="bed-chart__state">Loading chart...</div>}
        {!loading && error && (
          <div className="bed-chart__state bed-chart__state--error">Failed to load chart</div>
        )}
        {!loading && !error && model.chartData.length === 0 && (
          <div
            className="bed-chart__state bed-chart__state--empty"
            aria-label="Backtest chart is waiting for the first equity point"
          />
        )}
        {!loading && !error && model.chartData.length > 0 && (
          <EquityChart
            data={model.chartData}
            height={height}
            valueKey={isCompletedChart ? 'returnPct' : 'y'}
            benchmarkKey={showBenchmark && model.hasBenchmark ? (isCompletedChart ? 'benchmarkReturnPct' : 'benchmark') : undefined}
            color={chartColor}
            domain={isCompletedChart ? model.chartReturnDomain : model.chartDomain}
            showArea={isCompletedChart}
            areaBase={0}
            formatEndpoint={isCompletedChart ? formatPercentValue : undefined}
            xAxis={{
              dataKey: 'chartX',
              type: model.isPlayback ? 'number' : 'category',
              domain: model.isPlayback ? [0, 100] : undefined,
              ticks: model.isPlayback ? REPLAY_X_AXIS_TICKS : undefined,
              tick: { fill: 'var(--backtest-muted, #888)', fontSize: 10 },
              tickFormatter,
              stroke: 'var(--backtest-border, rgba(255,255,255,0.2))',
              interval: 'preserveStartEnd',
              minTickGap: model.isPlayback ? 20 : 36,
              allowDataOverflow: model.isPlayback,
            }}
            yAxis={{
              tick: { fill: 'var(--backtest-muted, #888)', fontSize: 10 },
              tickFormatter: (value) => isCompletedChart ? `${(value * 100).toFixed(0)}%` : `$${(value / 1000).toFixed(0)}k`,
              stroke: 'var(--backtest-border, rgba(255,255,255,0.2))',
            }}
          >
            <RechartsTooltip content={<BacktestEquityTooltip showReturn={isCompletedChart} />} />
            {isCompletedChart && <ReferenceLine y={0} stroke="var(--backtest-border, rgba(255,255,255,0.44))" strokeDasharray="6 5" />}
              {model.isPlayback && (
                <Line
                  type="monotone"
                  dataKey="currentY"
                  stroke="transparent"
                  strokeWidth={0}
                  dot={{
                    r: 3,
                    fill: chartColor,
                    stroke: 'var(--backtest-surface, #0b111c)',
                    strokeWidth: 2,
                  }}
                  activeDot={false}
                  isAnimationActive={false}
                />
              )}
          </EquityChart>
        )}
      </div>

      {showMetrics && model.metrics && (
        <MetricsRow
          metrics={model.metrics}
          showToggle={false}
          size={metricsSize}
          windowDays={model.elapsedDays}
        />
      )}
    </div>
  );
}
